import { weekFileName } from '../lib/weekFileName';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useDisplayCurrency } from './useDisplayCurrency';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Custom hook for managing expenses data and operations
 */
export const useExpenses = (selectedProject, statusFilter, dateFrom = null, dateTo = null, employeeFilter = 'all', categoryFilter = 'all') => {
  const { t } = useTranslation();
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchExpenses = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/expenses`;
      const params = [];
      if (selectedProject !== 'all') params.push(`project_id=${selectedProject}`);
      if (statusFilter !== 'all') params.push(`status=${statusFilter}`);
      if (dateFrom) params.push(`date_from=${dateFrom}`);
      if (dateTo) params.push(`date_to=${dateTo}`);
      if (employeeFilter !== 'all') params.push(`assigned_employee_id=${employeeFilter}`);
      if (categoryFilter !== 'all') params.push(`category=${categoryFilter}`);
      if (params.length > 0) url += '?' + params.join('&');
      
      const response = await axios.get(url);
      setExpenses(response.data);
    } catch (error) {
      console.error('Error fetching expenses:', error);
      toast.error(i18n.t('auto.misc3.failedToFetchExpenses'));
    } finally {
      setLoading(false);
    }
  }, [selectedProject, statusFilter, dateFrom, dateTo, employeeFilter, categoryFilter]);

  useEffect(() => {
    fetchExpenses();
  }, [fetchExpenses]);

  return { expenses, loading, refetch: fetchExpenses };
};

/**
 * Custom hook for managing budget summaries
 */
export const useBudgetSummaries = (projects, isAdminOrManager, dateRange = null) => {
  const { currency: displayCurrency } = useDisplayCurrency();
  const [budgetSummaries, setBudgetSummaries] = useState({});
  const [budgetLoading, setBudgetLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState({ loaded: 0, total: 0 });

  const fetchBudgetSummaries = useCallback(async () => {
    console.log('[useBudgetSummaries] Starting parallel fetch:', { 
      isAdminOrManager, 
      projectsCount: projects.length,
      dateRange,
      displayCurrency,
    });
    
    if (!isAdminOrManager || projects.length === 0) {
      console.log('[useBudgetSummaries] Skipping - no permission or no projects');
      return;
    }
    
    setBudgetLoading(true);
    setLoadingProgress({ loaded: 0, total: projects.length });
    
    try {
      // Build query params for date filter + display currency
      const params = new URLSearchParams();
      if (dateRange?.startDate) {
        params.append('start_date', dateRange.startDate);
      }
      if (dateRange?.endDate) {
        params.append('end_date', dateRange.endDate);
      }
      if (displayCurrency) {
        params.append('display_currency', displayCurrency);
      }
      const queryString = params.toString() ? '?' + params.toString() : '';
      
      // Fetch ALL projects in parallel using Promise.allSettled
      const fetchPromises = projects.map(async (project) => {
        const url = `${API}/projects/${project.id}/budget-summary${queryString}`;
        try {
          const response = await axios.get(url);
          return { projectId: project.id, data: response.data, success: true };
        } catch (error) {
          console.error(`Error fetching budget for ${project.name}:`, error);
          return { projectId: project.id, data: null, success: false };
        }
      });
      
      // Wait for all requests to complete
      const results = await Promise.allSettled(fetchPromises);
      
      // Process results into summaries object
      const summaries = {};
      let loadedCount = 0;
      
      for (const result of results) {
        if (result.status === 'fulfilled' && result.value.success) {
          summaries[result.value.projectId] = result.value.data;
          loadedCount++;
        }
      }
      
      console.log('[useBudgetSummaries] Done, loaded:', loadedCount, 'of', projects.length, 'projects');
      setBudgetSummaries(summaries);
      setLoadingProgress({ loaded: loadedCount, total: projects.length });
    } catch (error) {
      console.error('[useBudgetSummaries] Unexpected error:', error);
    } finally {
      setBudgetLoading(false);
    }
  }, [projects, isAdminOrManager, dateRange?.startDate, dateRange?.endDate, displayCurrency]);

  // Refetch when date range changes OR projects change
  useEffect(() => {
    fetchBudgetSummaries();
  }, [fetchBudgetSummaries]);

  return { budgetSummaries, budgetLoading, loadingProgress, refetch: fetchBudgetSummaries };
};

/**
 * Custom hook for expense CRUD operations
 */
export const useExpenseOperations = (refetchExpenses, refetchBudgets) => {
  
  const createExpense = async (expenseData, receiptFiles = []) => {
    try {
      const response = await axios.post(`${API}/expenses`, {
        ...expenseData,
        amount: parseFloat(expenseData.amount),
        expense_date: new Date(expenseData.expense_date).toISOString()
      });
      
      const expenseId = response.data.id;
      
      // Upload multiple receipts if provided
      if (receiptFiles && receiptFiles.length > 0) {
        let uploadSuccess = 0;
        let uploadFailed = 0;
        
        for (const file of receiptFiles) {
          try {
            const formData = new FormData();
            formData.append('file', file);
            await axios.post(`${API}/expenses/${expenseId}/receipt`, formData);
            uploadSuccess++;
          } catch (err) {
            console.error('Failed to upload receipt:', err);
            uploadFailed++;
          }
        }
        
        if (uploadFailed > 0) {
          toast.warning(`Expense created. ${uploadSuccess} receipt(s) uploaded, ${uploadFailed} failed.`);
        } else {
          toast.success(`Expense created with ${uploadSuccess} receipt(s)`);
        }
      } else {
        toast.success(i18n.t('auto.misc3.expenseCreatedSuccessfully'));
      }
      
      refetchExpenses();
      return { success: true, id: expenseId };
    } catch (error) {
      console.error('Error creating expense:', error);
      toast.error(error.response?.data?.detail || 'Failed to create expense');
      return { success: false, error };
    }
  };

  const submitExpense = async (expenseId) => {
    try {
      await axios.post(`${API}/expenses/${expenseId}/submit`);
      toast.success(i18n.t('auto.misc3.expenseSubmittedForApproval'));
      refetchExpenses();
      return { success: true };
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit expense');
      return { success: false, error };
    }
  };

  const deleteExpense = async (expenseId) => {
    try {
      await axios.delete(`${API}/expenses/${expenseId}`);
      toast.success(i18n.t('auto.misc3.expenseDeleted'));
      refetchExpenses();
      refetchBudgets?.();
      return { success: true };
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete expense');
      return { success: false, error };
    }
  };

  const approveExpense = async (expenseId, action, comments) => {
    try {
      await axios.post(`${API}/expenses/${expenseId}/approve`, {
        action,
        comments
      });
      toast.success(action === 'approve' ? i18n.t('auto.misc3.expenseApproved') : i18n.t('auto.misc3.expenseRejected'));
      refetchExpenses();
      refetchBudgets?.();
      return { success: true };
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process approval');
      return { success: false, error };
    }
  };

  const changeExpenseStatus = async (expenseId, action, comment = '') => {
    try {
      await axios.post(`${API}/expenses/${expenseId}/change-status`, {
        action,
        comment
      });
      
      const messages = {
        revert_draft: 'Expense reverted to draft',
        reject: 'Expense rejected',
        mark_paid: 'Expense marked as paid'
      };
      
      toast.success(messages[action] || 'Status updated');
      refetchExpenses();
      refetchBudgets?.();
      return { success: true };
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change status');
      return { success: false, error };
    }
  };

  return {
    createExpense,
    submitExpense,
    deleteExpense,
    approveExpense,
    changeExpenseStatus
  };
};

/**
 * Custom hook for batch expense operations
 */
export const useBatchExpenseOperations = (refetchExpenses) => {
  
  const createBatchExpenses = async (batchExpenses, submitImmediately = false) => {
    try {
      const expensesToSubmit = batchExpenses.map(exp => ({
        project_id: exp.project_id,
        expense_type: exp.expense_type,
        category: exp.category,
        subcategory: exp.subcategory,
        amount: parseFloat(exp.amount),
        currency: exp.currency,
        expense_date: new Date(exp.expense_date).toISOString(),
        description: exp.description,
        vendor: exp.vendor
      }));

      const response = await axios.post(`${API}/expenses/batch`, {
        expenses: expensesToSubmit,
        submit_immediately: submitImmediately
      });

      const { created, errors, expense_ids } = response.data;
      
      // Upload multiple receipts for each expense
      if (expense_ids && expense_ids.length > 0) {
        let totalUploadSuccess = 0;
        let totalUploadFailed = 0;
        
        for (let i = 0; i < batchExpenses.length; i++) {
          const exp = batchExpenses[i];
          const expenseId = expense_ids[i];
          
          if (exp.receipts && exp.receipts.length > 0 && expenseId) {
            for (const file of exp.receipts) {
              try {
                const formData = new FormData();
                formData.append('file', file);
                await axios.post(`${API}/expenses/${expenseId}/receipt`, formData, {
                  headers: { 'Content-Type': 'multipart/form-data' }
                });
                totalUploadSuccess++;
              } catch (err) {
                console.error('Failed to upload receipt:', err);
                totalUploadFailed++;
              }
            }
          }
        }
        
        if (totalUploadFailed > 0) {
          toast.warning(`Created ${created} expenses. ${totalUploadSuccess} receipts uploaded, ${totalUploadFailed} failed.`);
        } else if (totalUploadSuccess > 0) {
          toast.success(`Successfully created ${created} expense${created !== 1 ? 's' : ''} with ${totalUploadSuccess} receipt${totalUploadSuccess !== 1 ? 's' : ''}${submitImmediately ? ' and submitted for approval' : ''}`);
        } else {
          showBatchResult(created, errors, submitImmediately);
        }
      } else {
        showBatchResult(created, errors, submitImmediately);
      }

      refetchExpenses();
      return { success: true, created, errors };
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create expenses');
      return { success: false, error };
    }
  };

  const showBatchResult = (created, errors, submitImmediately) => {
    if (errors && errors.length > 0) {
      toast.warning(`Created ${created} expenses, but ${errors.length} failed`);
    } else {
      toast.success(`Successfully created ${created} expense${created !== 1 ? 's' : ''}${submitImmediately ? ' and submitted for approval' : ''}`);
    }
  };

  return { createBatchExpenses };
};

/**
 * Custom hook for receipt operations
 */
export const useReceiptOperations = (refetchExpenses) => {
  
  // View a single receipt
  const viewReceipt = async (expenseId, receiptId = null) => {
    try {
      const url = receiptId 
        ? `${API}/expenses/${expenseId}/receipt/${receiptId}`
        : `${API}/expenses/${expenseId}/receipt`;
        
      const response = await axios.get(url, {
        responseType: 'blob'
      });
      
      const contentType = response.headers['content-type'] || 'application/octet-stream';
      const blob = new Blob([response.data], { type: contentType });
      const blobUrl = window.URL.createObjectURL(blob);
      const newTab = window.open(blobUrl, '_blank');
      if (!newTab) {
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = 'receipt';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        toast.info(i18n.t('auto.misc3.popupBlockedFileDownloadedInstead'));
      }
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60000);
      return { success: true };
    } catch (error) {
      console.error('viewReceipt failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to load receipt');
      return { success: false, error };
    }
  };

  // View all receipts for an expense (opens each in new tab)
  const viewAllReceipts = async (expense) => {
    try {
      if (!expense || !expense.id) {
        console.error('[viewAllReceipts] Invalid expense object:', expense);
        toast.error(i18n.t('auto.misc3.invalidExpenseMissingId'));
        return { success: false };
      }
      
      console.log('[viewAllReceipts] Fetching receipts for expense:', expense.id);
      
      // First get the list of receipts
      const listResponse = await axios.get(`${API}/expenses/${expense.id}/receipts`);
      const receipts = listResponse.data.receipts || [];
      
      console.log('[viewAllReceipts] Found receipts:', receipts);
      
      if (receipts.length === 0) {
        toast.error(i18n.t('auto.misc3.noReceiptsFound'));
        return { success: false };
      }
      
      // Filter to only available receipts
      const availableReceipts = receipts.filter(r => r.available !== false);
      const lostCount = receipts.length - availableReceipts.length;
      
      if (availableReceipts.length === 0) {
        toast.error(i18n.t('auto.misc3.allReceiptFilesAreUnavailableThey'));
        return { success: false };
      }
      
      if (lostCount > 0) {
        toast.warning(`${lostCount} receipt(s) are unavailable (lost during migration). Showing ${availableReceipts.length} available receipt(s).`);
      }
      
      // For single receipt, just open it directly
      if (availableReceipts.length === 1) {
        const receipt = availableReceipts[0];
        const url = receipt.id === 'legacy'
          ? `${API}/expenses/${expense.id}/receipt`
          : `${API}/expenses/${expense.id}/receipt/${receipt.id}`;
        
        console.log('[viewAllReceipts] Fetching single receipt from URL:', url);
        const response = await axios.get(url, { responseType: 'blob' });
        const blob = new Blob([response.data], { type: response.headers['content-type'] });
        const blobUrl = window.URL.createObjectURL(blob);
        window.open(blobUrl, '_blank');
        setTimeout(() => window.URL.revokeObjectURL(blobUrl), 5000);
        return { success: true, count: 1 };
      }
      
      // For multiple receipts, open each with a delay to avoid popup blockers
      toast.info(`Opening ${availableReceipts.length} receipts...`);
      
      // Pre-fetch all receipts first
      const blobUrls = [];
      for (const receipt of availableReceipts) {
        const url = receipt.id === 'legacy'
          ? `${API}/expenses/${expense.id}/receipt`
          : `${API}/expenses/${expense.id}/receipt/${receipt.id}`;
        
        console.log('[viewAllReceipts] Fetching receipt from URL:', url);
        try {
          const response = await axios.get(url, { responseType: 'blob' });
          const blob = new Blob([response.data], { type: response.headers['content-type'] });
          const blobUrl = window.URL.createObjectURL(blob);
          blobUrls.push(blobUrl);
        } catch (err) {
          console.error(`[viewAllReceipts] Failed to fetch receipt ${receipt.id}:`, err);
          if (err.response?.status === 404) {
            console.warn(`[viewAllReceipts] Receipt ${receipt.id} file not found (likely lost)`);
          }
        }
      }
      
      // Open all tabs with small delays
      for (let i = 0; i < blobUrls.length; i++) {
        setTimeout(() => {
          const newWindow = window.open(blobUrls[i], '_blank');
          if (!newWindow) {
            toast.warning(i18n.t('auto.misc3.popupBlockedPleaseAllowPopupsFor'));
          }
        }, i * 300); // 300ms delay between each
      }
      
      // Clean up URLs after 10 seconds
      setTimeout(() => {
        blobUrls.forEach(url => window.URL.revokeObjectURL(url));
      }, 10000);
      
      return { success: true, count: blobUrls.length };
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error(i18n.t('auto.misc3.receiptFileNotFoundItMay'));
      } else {
        toast.error(error.response?.data?.detail || 'Failed to load receipts');
      }
      return { success: false, error };
    }
  };

  // Download all receipts as ZIP
  const downloadAllReceipts = async (expenseId) => {
    try {
      const response = await axios.get(`${API}/expenses/${expenseId}/receipts/download`, {
        responseType: 'blob'
      });
      
      // Get filename from Content-Disposition header or generate one
      const contentDisposition = response.headers['content-disposition'];
      let filename = `receipts_${expenseId}.zip`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename=(.+)/);
        if (filenameMatch) {
          filename = filenameMatch[1].replace(/"/g, '');
        }
      }
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', weekFileName(filename));
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(i18n.t('auto.misc3.receiptsDownloaded'));
      return { success: true };
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to download receipts');
      return { success: false, error };
    }
  };

  const deleteReceipt = async (expenseId, receiptId) => {
    try {
      await axios.delete(`${API}/expenses/${expenseId}/receipt/${receiptId}`);
      toast.success(i18n.t('auto.misc3.receiptDeleted'));
      refetchExpenses();
      return { success: true };
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete receipt');
      return { success: false, error };
    }
  };

  const reuploadReceipt = async (expenseId, receiptId, file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.put(`${API}/expenses/${expenseId}/receipt/${receiptId}/reupload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(i18n.t('auto.misc3.receiptReUploadedSuccessfully'));
      refetchExpenses();
      return { success: true };
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to re-upload receipt');
      return { success: false, error };
    }
  };

  return { viewReceipt, viewAllReceipts, downloadAllReceipts, deleteReceipt, reuploadReceipt };
};
